import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { authClient } from '@/lib/auth-client'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { RafobaseLogo } from '@/components/rafobase-logo'
import { CheckCircle2, XCircle } from 'lucide-react'
import { toast } from 'sonner'

type ResetPasswordSearch = {
  token?: string
  error?: string
}

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): ResetPasswordSearch => {
    return {
      token: (search.token as string) || undefined,
      error: (search.error as string) || undefined,
    }
  },
})

function ResetPasswordPage() {
  const navigate = useNavigate()
  const { token, error: urlError } = Route.useSearch()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Validation checks
  const hasMinLength = password.length >= 8
  const hasNumber = /\d/.test(password)
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)
  const passwordsMatch = password === confirmPassword && password.length > 0

  useEffect(() => {
    if (urlError === 'INVALID_TOKEN') {
      setError('O link de redefinição de senha é inválido ou expirou.')
    }
  }, [urlError])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!token) {
      setError('Token de redefinição não encontrado. Solicite um novo link.')
      return
    }

    if (!hasMinLength || !hasNumber || !hasSpecialChar) {
      setError('A senha não atende aos requisitos mínimos.')
      return
    }

    if (!passwordsMatch) {
      setError('As senhas não coincidem.')
      return
    }

    setIsLoading(true)

    try {
      const { error: resetError } = await authClient.resetPassword({
        newPassword: password,
        token,
      })

      if (resetError) {
        setError(resetError.message || 'Erro ao redefinir a senha')
        return
      }

      toast.success('Senha redefinida com sucesso!', {
        description: 'Você já pode fazer login com sua nova senha.',
      })

      navigate({ to: '/login' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao redefinir a senha')
    } finally {
      setIsLoading(false)
    }
  }

  if (!token && !urlError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <CardDescription className="text-destructive font-medium">
              Link Inválido
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="mb-6">O link que você acessou não contém um token válido.</p>
            <Button asChild className="w-full">
              <Link to="/forgot-password">Solicitar novo link</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <RafobaseLogo className="h-12 w-auto text-foreground" />
          </div>
          <CardDescription>Crie uma nova senha de acesso forte e segura</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">Nova Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirme a Nova Senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>

            <div className="text-sm space-y-2 mt-4 text-muted-foreground bg-muted/50 p-3 rounded-md">
              <p className="font-medium text-foreground mb-1">A senha deve conter:</p>
              <div className="flex items-center gap-2">
                {hasMinLength ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground/50" />
                )}
                <span className={hasMinLength ? 'text-foreground' : ''}>
                  Pelo menos 8 caracteres
                </span>
              </div>
              <div className="flex items-center gap-2">
                {hasNumber ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground/50" />
                )}
                <span className={hasNumber ? 'text-foreground' : ''}>Pelo menos 1 número</span>
              </div>
              <div className="flex items-center gap-2">
                {hasSpecialChar ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground/50" />
                )}
                <span className={hasSpecialChar ? 'text-foreground' : ''}>
                  Pelo menos 1 caractere especial
                </span>
              </div>
              <div className="flex items-center gap-2">
                {passwordsMatch ? (
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                ) : (
                  <XCircle className="w-4 h-4 text-muted-foreground/50" />
                )}
                <span className={passwordsMatch ? 'text-foreground' : ''}>As senhas coincidem</span>
              </div>
            </div>

            <ChonkyButton
              type="submit"
              className="w-full mt-6"
              disabled={
                isLoading || !hasMinLength || !hasNumber || !hasSpecialChar || !passwordsMatch
              }
              loading={isLoading}
            >
              Salvar Nova Senha
            </ChonkyButton>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
